import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Summarize as SummarizeIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
  Assignment as AssignmentIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { StatusSummaryBar, type StatusSummaryMetric } from '../common/StatusSummaryBar';
import type { PendingWorkResponse } from '../../types/pendingWork.types';
import { Box } from '@mui/material';
import { useNavigationPermissions } from '../../hooks/useNavigationPermissions';

export type PendingWorkBarProps = {
  pendingWork: PendingWorkResponse;
  loading?: boolean;
};

/**
 * Inicio inbox row: Total (not navigable) + per-menu pending cards.
 * Queues the API returns as `null` (no menu permission) are omitted — no count leak.
 * `disabledReason` remains as defense-in-depth if a count arrives without nav access.
 */
const noAccess = (menuName: string) => `Sin acceso a ${menuName}`;
export const PendingWorkBar: React.FC<PendingWorkBarProps> = ({ pendingWork, loading = false }) => {
  const navigate = useNavigate();
  const { canInventoryRegister, canInventoryTransfer, canInventoryRequest, canSales } =
    useNavigationPermissions();

  const metrics: StatusSummaryMetric[] = useMemo(() => {
    const items: StatusSummaryMetric[] = [
      {
        key: 'total',
        label: 'Total pendientes',
        icon: <SummarizeIcon />,
        iconVariant: 'inventory',
        filterValue: '',
      },
    ];

    if (pendingWork.records != null) {
      items.push({
        key: 'records',
        label: 'Registros pendientes',
        icon: <InventoryIcon />,
        iconVariant: 'warning',
        filterValue: 'records',
        disabledReason: canInventoryRegister ? undefined : noAccess('Registro de inventario'),
      });
    }
    if (pendingWork.transfers != null) {
      items.push({
        key: 'transfers',
        label: 'Transferencias pendientes',
        icon: <LocalShippingIcon />,
        iconVariant: 'warning',
        filterValue: 'transfers',
        disabledReason: canInventoryTransfer ? undefined : noAccess('Transferencias'),
      });
    }
    if (pendingWork.requests != null) {
      items.push({
        key: 'requests',
        label: 'Solicitudes pendientes',
        icon: <AssignmentIcon />,
        iconVariant: 'warning',
        filterValue: 'requests',
        disabledReason: canInventoryRequest ? undefined : noAccess('Solicitudes'),
      });
    }
    if (pendingWork.salesPendingPayment != null) {
      items.push({
        key: 'salesPendingPayment',
        label: 'Ventas por cobrar',
        icon: <PaymentsIcon />,
        iconVariant: 'warning',
        filterValue: 'sales',
        disabledReason: canSales ? undefined : noAccess('Gestión de ventas'),
      });
    }

    return items;
  }, [pendingWork, canInventoryRegister, canInventoryTransfer, canInventoryRequest, canSales]);

  const counts = useMemo(() => {
    const next: Record<string, number> = {
      total: pendingWork.total,
    };
    if (pendingWork.records != null) next.records = pendingWork.records;
    if (pendingWork.transfers != null) next.transfers = pendingWork.transfers;
    if (pendingWork.requests != null) next.requests = pendingWork.requests;
    if (pendingWork.salesPendingPayment != null) {
      next.salesPendingPayment = pendingWork.salesPendingPayment;
    }
    return next;
  }, [pendingWork]);

  const handleMetricClick = (filterValue: string) => {
    if (filterValue === '') return;

    switch (filterValue) {
      case 'records':
        navigate('/dashboard/inventory/register', {
          state: { filterRecordStatus: 'PENDING' },
        });
        break;
      case 'transfers':
        navigate('/dashboard/inventory/transfer', {
          state: { filterTransferStatus: 'PENDING' },
        });
        break;
      case 'requests':
        navigate('/dashboard/inventory/requests', {
          state: { filterRequestStatus: 'PENDING' },
        });
        break;
      case 'sales':
        navigate('/dashboard/orders', {
          state: { filterPostSalesKpi: 'pending_payment' },
        });
        break;
      default:
        break;
    }
  };

  const hasAnyQueue = metrics.some((m) => m.filterValue !== '' && m.disabledReason == null);

  return (
    <Box sx={{ mb: 2 }}>
      <StatusSummaryBar
        metrics={metrics}
        counts={counts}
        loading={loading}
        activeFilterValue=""
        emptyFilterClickable={false}
        onMetricClick={hasAnyQueue ? handleMetricClick : undefined}
      />
    </Box>
  );
};
